import { forwardRef, type ReactNode, useState } from 'react';
import { StyleSheet, type TextInput, type TextInputProps, View } from 'react-native';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';
import { fieldFocusBorderColor } from '../../theme/typography';
import { FormInput } from './FormInput';
import { VStack } from './Stack';
import { Text } from './Text';

interface InputProps extends TextInputProps {
  label?: string;
  error?: string;
  helperText?: string;
  /** Optional decorative icon rendered inside the field, left-aligned. */
  leftIcon?: ReactNode;
}

export const Input = forwardRef<TextInput, InputProps>(
  ({ label, error, helperText, leftIcon, style, onFocus, onBlur, ...props }, ref) => {
    const [focused, setFocused] = useState(false);
    const field = (
      <FormInput
        ref={ref}
        accessibilityLabel={label || props.placeholder}
        style={[
          styles.input,
          leftIcon ? styles.inputWithIcon : null,
          focused && styles.inputFocused,
          error && styles.inputError,
          style,
        ]}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        {...props}
      />
    );
    return (
      <VStack spacing="xs">
        {label && (
          <Text variant="bodySmall" color={colors.text.primary} style={{ fontWeight: '500' }}>
            {label}
          </Text>
        )}
        {leftIcon ? (
          <View style={styles.fieldWrap}>
            <View style={styles.leftIcon} pointerEvents="none">
              {leftIcon}
            </View>
            {field}
          </View>
        ) : (
          field
        )}
        {error && (
          <Text
            variant="caption"
            color={colors.status.error.main}
            accessibilityLiveRegion="assertive"
            accessibilityRole="alert"
          >
            {error}
          </Text>
        )}
        {helperText && !error && (
          <Text variant="caption" color={colors.text.secondary}>
            {helperText}
          </Text>
        )}
      </VStack>
    );
  },
);

Input.displayName = 'Input';

const styles = StyleSheet.create({
  input: {
    width: '100%',
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.background.primary,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: tokens.fontSize.base,
    minHeight: 44,
  },
  inputWithIcon: {
    paddingLeft: 42,
  },
  inputFocused: {
    borderColor: fieldFocusBorderColor,
  },
  inputError: {
    borderColor: colors.status.error.main,
  },
  fieldWrap: {
    position: 'relative',
    justifyContent: 'center',
  },
  leftIcon: {
    position: 'absolute',
    left: spacing.md,
    top: 0,
    bottom: 0,
    zIndex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
